import { polygonRings, pointInPolygonRing } from "@constructionautomation/backend/convex/lib/measurementGeometry";
import {
  useCallback,
  type Dispatch,
  type MouseEvent as ReactMouseEvent,
  type MutableRefObject,
  type SetStateAction,
} from "react";

import type { Id } from "@constructionautomation/backend/convex/_generated/dataModel";
import type { DocumentViewportValue } from "@/features/document-viewport/types";
import type { MeasurementScale } from "@/hooks/useMeasurementScale";
import type { Point } from "@/lib/geometry";
import { undoRedoErrorMessage } from "@/lib/undo-redo-error";
import { TOOL_IDS, type MeasurementType, type ToolType } from "@/viewport-tools/measurement";
import { getMeasurementGeometryBounds } from "@/viewport-tools/measurement/overlay/measurement-overlay-helpers";

import { commitPreviewMeasurementUpdates } from "./commit-measurement-updates";
import {
  finalizeDrawingInteraction,
  handleDrawingPointerDownInteraction,
  updateDrawingPreviewInteraction,
} from "./drawing-interactions";
import { applyTransformPreviewOnPointerMove } from "./transform-interactions";
import type { InteractionState, MeasurementOverlayContextMenuState } from "./types";
import type {
  DraftMeasurementState,
  MeasurementAnnotation,
  MeasurementGeometry,
  MeasurementOverlayRecord,
  PolygonGeometry,
} from "../measurement-overlay-types";

interface UseMeasurementOverlayStageHandlersArgs {
  viewport: DocumentViewportValue;
  scale: MeasurementScale | null;
  activeTool: ToolType | null;
  activeMeasurementType: MeasurementType | null;
  snapEnabled: boolean;
  angleLockEnabled: boolean;
  incrementSnapEnabled: boolean;
  incrementStepFeet: number;
  measurements: MeasurementOverlayRecord[];
  selectedMeasurementIds: Id<"takeoffPilotMeasurements">[];
  previewAnnotations: Record<string, MeasurementAnnotation>;
  previewGeometries: Record<string, MeasurementGeometry | PolygonGeometry>;
  interaction: InteractionState | null;
  interactionRef: MutableRefObject<InteractionState | null>;
  drawingDraftRef: MutableRefObject<DraftMeasurementState | null>;
  latestDraftRef: MutableRefObject<DraftMeasurementState | null>;
  lastPointerPagePointRef: MutableRefObject<Point | null>;
  isSpacePressedRef: MutableRefObject<boolean>;
  getPagePoint: (clientX: number, clientY: number) => Point | null;
  getScreenDistance: (pointA: Point, pointB: Point) => number;
  getActiveHeight: () => number | undefined;
  getSnappedPagePoint: (point: Point) => Point;
  updateSnapHoverPoint: (
    point: Point | null,
    options?: { filter?: (point: Point) => boolean }
  ) => Point | null;
  clearSnapHoverPoint: () => void;
  snapSegmentPoint: (
    anchor: Point,
    candidate: Point,
    options?: {
      allowPdfSnap?: boolean;
      lockPdfSnapToCandidateRay?: boolean;
    }
  ) => Point;
  snapRectangleCorner: (
    anchor: Point,
    candidate: Point,
    lockSquare: boolean,
    options?: { allowPdfSnap?: boolean }
  ) => Point;
  updateUnsavedDraft: (draft: DraftMeasurementState) => void;
  onDraftMeasurementChange: (draft: DraftMeasurementState | null) => void;
  onSelectMeasurement: (
    measurementId: Id<"takeoffPilotMeasurements"> | null,
    options?: { shiftKey?: boolean }
  ) => void;
  onSelectMeasurements?: (
    measurementIds: Id<"takeoffPilotMeasurements">[],
    options?: { additive?: boolean }
  ) => void;
  onUpdateMeasurement: (
    measurementId: Id<"takeoffPilotMeasurements">,
    geometry: MeasurementGeometry | PolygonGeometry,
    height: number | undefined,
    value: number
  ) => Promise<void>;
  onUpdateMeasurementAnnotation?: (
    measurementId: Id<"takeoffPilotMeasurements">,
    annotation: MeasurementAnnotation
  ) => Promise<void>;
  handleCancelDraft: () => void;
  clearSelection: () => void;
  setInteraction: Dispatch<SetStateAction<InteractionState | null>>;
  setContextMenu: Dispatch<SetStateAction<MeasurementOverlayContextMenuState | null>>;
  setDrawingDraft: Dispatch<SetStateAction<DraftMeasurementState | null>>;
  setPreviewAnnotations: Dispatch<SetStateAction<Record<string, MeasurementAnnotation>>>;
  setPreviewGeometries: Dispatch<
    SetStateAction<Record<string, MeasurementGeometry | PolygonGeometry>>
  >;
  setErrorMessage: Dispatch<SetStateAction<string | null>>;
}

export function useMeasurementOverlayStageHandlers({
  viewport,
  scale,
  activeTool,
  activeMeasurementType,
  snapEnabled,
  angleLockEnabled,
  incrementSnapEnabled,
  incrementStepFeet,
  measurements,
  selectedMeasurementIds,
  previewAnnotations,
  previewGeometries,
  interaction,
  interactionRef,
  drawingDraftRef,
  latestDraftRef,
  lastPointerPagePointRef,
  isSpacePressedRef,
  getPagePoint,
  getScreenDistance,
  getActiveHeight,
  getSnappedPagePoint,
  updateSnapHoverPoint,
  clearSnapHoverPoint,
  snapSegmentPoint,
  snapRectangleCorner,
  updateUnsavedDraft,
  onDraftMeasurementChange,
  onSelectMeasurement,
  onSelectMeasurements,
  onUpdateMeasurement,
  onUpdateMeasurementAnnotation,
  handleCancelDraft,
  clearSelection,
  setInteraction,
  setContextMenu,
  setDrawingDraft,
  setPreviewAnnotations,
  setPreviewGeometries,
  setErrorMessage,
}: UseMeasurementOverlayStageHandlersArgs) {
  const clearInteractionState = useCallback(() => {
    interactionRef.current = null;
    setInteraction(null);
  }, [interactionRef, setInteraction]);

  const finalizeDrawing = useCallback(
    () =>
      finalizeDrawingInteraction({
        drawingDraftRef,
        viewport,
        setErrorMessage,
        setDrawingDraft,
        interactionRef,
        setInteraction,
        latestDraftRef,
        onDraftMeasurementChange,
      }),
    [
      drawingDraftRef,
      interactionRef,
      latestDraftRef,
      onDraftMeasurementChange,
      setDrawingDraft,
      setErrorMessage,
      setInteraction,
      viewport,
    ]
  );

  const handleStageMouseDown = useCallback(
    (event: ReactMouseEvent<HTMLDivElement>) => {
      const activeInteraction = interactionRef.current ?? interaction;
      if (event.button === 2) {
        return;
      }
      setContextMenu(null);

      if (isSpacePressedRef.current) {
        clearSnapHoverPoint();
        const nextInteraction: InteractionState = {
          mode: "pan",
          startClientX: event.clientX,
          startClientY: event.clientY,
          initialPanX: viewport.camera.pan.x,
          initialPanY: viewport.camera.pan.y,
          previousInteraction:
            activeInteraction?.mode === "drawing" ? activeInteraction : undefined,
        };
        interactionRef.current = nextInteraction;
        setInteraction(nextInteraction);
        return;
      }

      if ((event.ctrlKey || event.metaKey || (!activeTool && event.shiftKey)) && !latestDraftRef.current) {
        const pagePoint = getPagePoint(event.clientX, event.clientY);
        if (!pagePoint) {
          return;
        }
        event.preventDefault();
        clearSnapHoverPoint();
        const nextInteraction: InteractionState = {
          mode: "box-select",
          startPoint: pagePoint,
          currentPoint: pagePoint,
        };
        lastPointerPagePointRef.current = pagePoint;
        interactionRef.current = nextInteraction;
        setInteraction(nextInteraction);
        return;
      }

      if (
        selectedMeasurementIds.length > 0 &&
        !latestDraftRef.current &&
        activeInteraction?.mode !== "drawing"
      ) {
        clearSelection();
        return;
      }

      const handledByDrawingMode = handleDrawingPointerDownInteraction({
        event,
        activeInteraction,
        activeTool,
        activeMeasurementType,
        scale,
        getPagePoint,
        setLastPointerPagePoint: (point) => {
          lastPointerPagePointRef.current = point;
        },
        onSelectMeasurement: (measurementId) => onSelectMeasurement(measurementId),
        handleCancelDraft,
        setErrorMessage,
        interactionRef,
        setInteraction,
        drawingDraftRef,
        setDrawingDraft,
        getActiveHeight,
        getScreenDistance,
        snapEnabled,
        angleLockEnabled,
        valueIncrementFeet: incrementSnapEnabled ? incrementStepFeet : undefined,
        getSnappedPagePoint,
        snapSegmentPoint,
        snapRectangleCorner,
        finalizeDrawing,
      });

      if (handledByDrawingMode) {
        return;
      }
    },
    [
      activeMeasurementType,
      activeTool,
      clearSnapHoverPoint,
      clearSelection,
      drawingDraftRef,
      finalizeDrawing,
      getActiveHeight,
      getPagePoint,
      getScreenDistance,
      getSnappedPagePoint,
      handleCancelDraft,
      interaction,
      interactionRef,
      isSpacePressedRef,
      latestDraftRef,
      onSelectMeasurement,
      scale,
      selectedMeasurementIds.length,
      setContextMenu,
      setDrawingDraft,
      setErrorMessage,
      setInteraction,
      snapEnabled,
      angleLockEnabled,
      incrementSnapEnabled,
      incrementStepFeet,
      snapSegmentPoint,
      snapRectangleCorner,
      viewport.camera.pan.x,
      viewport.camera.pan.y,
      lastPointerPagePointRef,
    ]
  );

  const handleStageContextMenu = useCallback(
    (event: ReactMouseEvent<HTMLDivElement>) => {
      event.preventDefault();
      event.stopPropagation();
      const pagePoint = getPagePoint(event.clientX, event.clientY);
      if (pagePoint) {
        lastPointerPagePointRef.current = pagePoint;
      }
      const overlayRect = event.currentTarget.getBoundingClientRect();
      handleCancelDraft();
      setContextMenu({
        target: { type: "document" },
        x: event.clientX - overlayRect.left,
        y: event.clientY - overlayRect.top,
        pagePoint,
      });
    },
    [getPagePoint, handleCancelDraft, lastPointerPagePointRef, setContextMenu]
  );

  const handleStageMouseMove = useCallback(
    (event: ReactMouseEvent<HTMLDivElement>) => {
      const activeInteraction = interactionRef.current ?? interaction;

      // Pan uses client coordinates only; do not require a page hit-test.
      if (activeInteraction?.mode === "pan") {
        const pagePoint = getPagePoint(event.clientX, event.clientY);
        if (pagePoint) {
          lastPointerPagePointRef.current = pagePoint;
        }
        clearSnapHoverPoint();
        applyTransformPreviewOnPointerMove({
          activeInteraction,
          event,
          pagePoint: lastPointerPagePointRef.current ?? { x: 0, y: 0 },
          viewport,
          measurements,
          snapEnabled,
          updateSnapHoverPoint,
          snapSegmentPoint,
          snapRectangleCorner,
          setPreviewGeometries,
          setPreviewAnnotations,
          updateUnsavedDraft,
        });
        return;
      }

      const pagePoint = getPagePoint(event.clientX, event.clientY);
      if (!pagePoint) {
        clearSnapHoverPoint();
        return;
      }
      lastPointerPagePointRef.current = pagePoint;

      if (event.shiftKey) {
        clearSnapHoverPoint();
      }

      if (!activeInteraction) {
        if (
          activeTool &&
          activeMeasurementType &&
          snapEnabled &&
          !event.shiftKey
        ) {
          updateSnapHoverPoint(pagePoint);
        } else {
          clearSnapHoverPoint();
        }
        return;
      }

      if (activeInteraction.mode === "box-select") {
        clearSnapHoverPoint();
        const nextInteraction: InteractionState = {
          ...activeInteraction,
          currentPoint: pagePoint,
        };
        interactionRef.current = nextInteraction;
        setInteraction(nextInteraction);
        return;
      }

      if (
        activeInteraction.mode === "move" ||
        activeInteraction.mode === "move-selection" ||
        activeInteraction.mode === "move-draft" ||
        activeInteraction.mode === "move-label" ||
        activeInteraction.mode === "resize-label"
      ) {
        // These modes do not refresh snap hover; clear so a pre-drag target
        // cannot reappear as a stale crosshair during/after the interaction.
        clearSnapHoverPoint();
      }

      if (
        applyTransformPreviewOnPointerMove({
          activeInteraction,
          event,
          pagePoint,
          viewport,
          measurements,
          snapEnabled,
          updateSnapHoverPoint,
          snapSegmentPoint,
          snapRectangleCorner,
          setPreviewGeometries,
          setPreviewAnnotations,
          updateUnsavedDraft,
        })
      ) {
        return;
      }

      if (activeInteraction.mode === "drawing") {
        updateDrawingPreviewInteraction({
          activeInteraction,
          activeTool,
          activeMeasurementType,
          snapEnabled,
          angleLockEnabled,
          valueIncrementFeet: incrementSnapEnabled ? incrementStepFeet : undefined,
          getActiveHeight,
          scale,
          snapSegmentPoint,
          snapRectangleCorner,
          pagePoint,
          shiftKey: event.shiftKey,
          drawingDraftRef,
          setDrawingDraft,
        });
      }
    },
    [
      interaction,
      interactionRef,
      getPagePoint,
      updateSnapHoverPoint,
      clearSnapHoverPoint,
      lastPointerPagePointRef,
      viewport,
      measurements,
      incrementSnapEnabled,
      incrementStepFeet,
      scale,
      snapSegmentPoint,
      snapRectangleCorner,
      setPreviewGeometries,
      updateUnsavedDraft,
      activeTool,
      activeMeasurementType,
      angleLockEnabled,
      snapEnabled,
      getActiveHeight,
      drawingDraftRef,
      setDrawingDraft,
      setInteraction,
    ]
  );

  const handleStageMouseUp = useCallback(async () => {
    const activeInteraction = interactionRef.current ?? interaction;
    if (!activeInteraction) {
      return;
    }

    if (activeInteraction.mode === "drawing") {
      return;
    }

    if (activeInteraction.mode === "pan") {
      if (activeInteraction.previousInteraction) {
        interactionRef.current = activeInteraction.previousInteraction;
        setInteraction(activeInteraction.previousInteraction);
      } else {
        clearInteractionState();
      }
      return;
    }

    if (activeInteraction.mode === "box-select") {
      const dragDistance = getScreenDistance(activeInteraction.startPoint, activeInteraction.currentPoint);
      if (dragDistance >= 3) {
        const selectionBounds = getSelectionBounds(activeInteraction.startPoint, activeInteraction.currentPoint);
        const selectedIds = measurements
          .filter((measurement) => measurementIntersectsSelection(measurement, selectionBounds))
          .map((measurement) => measurement._id);

        if (selectedIds.length > 0) {
          onSelectMeasurements?.(selectedIds, { additive: true });
        }
      }
      clearInteractionState();
      return;
    }

    if (activeInteraction.mode === "move-draft") {
      clearInteractionState();
      return;
    }

    const clearAnnotationPreview = (measurementId: Id<"takeoffPilotMeasurements">, preview: MeasurementAnnotation | undefined) => {
      const currentInteraction = interactionRef.current;
      if (currentInteraction === activeInteraction) clearInteractionState();
      if (currentInteraction !== activeInteraction &&
        (currentInteraction?.mode === "move-label" || currentInteraction?.mode === "resize-label") &&
        currentInteraction.measurementId === measurementId) return;
      setPreviewAnnotations((current) => {
        const key = String(measurementId);
        if (current[key] !== preview) return current;
        const remaining = { ...current };
        delete remaining[key];
        return remaining;
      });
    };

    if (activeInteraction.mode === "move-label") {
      const nextAnnotation = previewAnnotations[String(activeInteraction.measurementId)];
      try {
        if (nextAnnotation && onUpdateMeasurementAnnotation) {
          await onUpdateMeasurementAnnotation(activeInteraction.measurementId, {
            textOffset: nextAnnotation.textOffset,
            ...(activeInteraction.initialAnnotation?.leaderVisible === undefined && nextAnnotation.leaderVisible
              ? { leaderVisible: true }
              : {}),
          });
        }
      } catch (error) {
        console.error("Failed to commit preview measurement annotation updates:", error);
        setErrorMessage(undoRedoErrorMessage(error, "Failed to update measurement label"));
      } finally {
        clearAnnotationPreview(activeInteraction.measurementId, nextAnnotation);
      }
      return;
    }

    if (activeInteraction.mode === "resize-label") {
      const nextAnnotation = previewAnnotations[String(activeInteraction.measurementId)];
      try {
        if (nextAnnotation && onUpdateMeasurementAnnotation) {
          await onUpdateMeasurementAnnotation(activeInteraction.measurementId, {
            textBoxWidth: nextAnnotation.textBoxWidth,
          });
        }
      } catch (error) {
        console.error("Failed to commit preview measurement label resize:", error);
        setErrorMessage(undoRedoErrorMessage(error, "Failed to resize measurement label"));
      } finally {
        clearAnnotationPreview(activeInteraction.measurementId, nextAnnotation);
      }
      return;
    }

    try {
      await commitPreviewMeasurementUpdates({
        previewGeometries,
        measurements,
        scale,
        valueIncrementFeet: incrementSnapEnabled ? incrementStepFeet : undefined,
        preserveMeasurementValues:
          activeInteraction.mode === "move" || activeInteraction.mode === "move-selection",
        onUpdateMeasurement,
      });
    } catch (error) {
      console.error("Failed to commit preview measurement updates:", error);
      setErrorMessage("Failed to update measurement");
    } finally {
      if (interactionRef.current === activeInteraction) clearInteractionState();
      setPreviewGeometries((current) => {
        const remaining = { ...current };
        for (const [id, geometry] of Object.entries(previewGeometries)) {
          if (remaining[id] === geometry) delete remaining[id];
        }
        return remaining;
      });
    }
  }, [
    clearInteractionState,
    getScreenDistance,
    interaction,
    measurements,
    onSelectMeasurements,
    onUpdateMeasurementAnnotation,
    onUpdateMeasurement,
    previewAnnotations,
    previewGeometries,
    scale,
    incrementSnapEnabled,
    incrementStepFeet,
    setErrorMessage,
    setPreviewAnnotations,
    setPreviewGeometries,
  ]);

  const handleStageMouseLeave = useCallback(() => {
    clearSnapHoverPoint();
    if (!interactionRef.current && !interaction) {
      return;
    }
    void handleStageMouseUp();
  }, [clearSnapHoverPoint, handleStageMouseUp, interaction, interactionRef]);

  return {
    handleStageMouseDown,
    handleStageContextMenu,
    handleStageMouseMove,
    handleStageMouseUp,
    handleStageMouseLeave,
    finalizeDrawing,
  };
}

type SelectionBounds = ReturnType<typeof getSelectionBounds>;

function getSelectionBounds(startPoint: Point, currentPoint: Point) {
  return {
    minX: Math.min(startPoint.x, currentPoint.x),
    minY: Math.min(startPoint.y, currentPoint.y),
    maxX: Math.max(startPoint.x, currentPoint.x),
    maxY: Math.max(startPoint.y, currentPoint.y),
  };
}

function measurementIntersectsSelection(
  measurement: MeasurementOverlayRecord,
  selectionBounds: SelectionBounds
) {
  const { geometry } = measurement;
  const geometryBounds = getMeasurementGeometryBounds(geometry);
  if (!boundsIntersect(geometryBounds, selectionBounds)) {
    return false;
  }

  if (geometry.type === "rect") {
    return true;
  }

  const rings = measurement.tool === TOOL_IDS.polygon ? polygonRings(geometry) : [geometry.vertices];
  if (rings.some(ring => ring.some((vertex) => pointInBounds(vertex, selectionBounds)))) {
    return true;
  }

  const isClosed = measurement.tool === TOOL_IDS.polygon;
  if (rings.some(ring => polySegmentsIntersectBounds({ type: "poly", vertices: ring }, selectionBounds, isClosed))) {
    return true;
  }

  return isClosed && getBoundsCorners(selectionBounds).some((corner) => pointInPolygon(corner, geometry.vertices) &&
    !(geometry.holes ?? []).some(hole => pointInPolygonRing(corner, hole)));
}

function boundsIntersect(
  first: SelectionBounds,
  second: SelectionBounds
) {
  return first.maxX >= second.minX && first.minX <= second.maxX && first.maxY >= second.minY && first.minY <= second.maxY;
}

function pointInBounds(point: Point, bounds: SelectionBounds) {
  return point.x >= bounds.minX && point.x <= bounds.maxX && point.y >= bounds.minY && point.y <= bounds.maxY;
}

function polySegmentsIntersectBounds(poly: PolygonGeometry, bounds: SelectionBounds, closed: boolean) {
  if (poly.vertices.length < 2) {
    return false;
  }

  const boundsEdges = getBoundsEdges(bounds);
  const segmentCount = closed ? poly.vertices.length : poly.vertices.length - 1;
  for (let index = 0; index < segmentCount; index += 1) {
    const start = poly.vertices[index];
    const end = poly.vertices[(index + 1) % poly.vertices.length];
    if (boundsEdges.some(([edgeStart, edgeEnd]) => segmentsIntersect(start, end, edgeStart, edgeEnd))) {
      return true;
    }
  }

  return false;
}

function getBoundsCorners(bounds: SelectionBounds): Point[] {
  return [
    { x: bounds.minX, y: bounds.minY },
    { x: bounds.maxX, y: bounds.minY },
    { x: bounds.maxX, y: bounds.maxY },
    { x: bounds.minX, y: bounds.maxY },
  ];
}

function getBoundsEdges(bounds: SelectionBounds): Array<[Point, Point]> {
  const [topLeft, topRight, bottomRight, bottomLeft] = getBoundsCorners(bounds);
  return [
    [topLeft, topRight],
    [topRight, bottomRight],
    [bottomRight, bottomLeft],
    [bottomLeft, topLeft],
  ];
}

function segmentsIntersect(firstStart: Point, firstEnd: Point, secondStart: Point, secondEnd: Point) {
  const firstDirection = direction(firstStart, firstEnd, secondStart);
  const secondDirection = direction(firstStart, firstEnd, secondEnd);
  const thirdDirection = direction(secondStart, secondEnd, firstStart);
  const fourthDirection = direction(secondStart, secondEnd, firstEnd);

  if (firstDirection * secondDirection < 0 && thirdDirection * fourthDirection < 0) {
    return true;
  }

  return (
    (isZero(firstDirection) && pointOnSegment(secondStart, firstStart, firstEnd)) ||
    (isZero(secondDirection) && pointOnSegment(secondEnd, firstStart, firstEnd)) ||
    (isZero(thirdDirection) && pointOnSegment(firstStart, secondStart, secondEnd)) ||
    (isZero(fourthDirection) && pointOnSegment(firstEnd, secondStart, secondEnd))
  );
}

function direction(start: Point, end: Point, point: Point) {
  return (point.x - start.x) * (end.y - start.y) - (point.y - start.y) * (end.x - start.x);
}

function isZero(value: number) {
  return Math.abs(value) <= 1e-9;
}

function pointOnSegment(point: Point, start: Point, end: Point) {
  return (
    point.x >= Math.min(start.x, end.x) - 1e-9 &&
    point.x <= Math.max(start.x, end.x) + 1e-9 &&
    point.y >= Math.min(start.y, end.y) - 1e-9 &&
    point.y <= Math.max(start.y, end.y) + 1e-9
  );
}

function pointInPolygon(point: Point, vertices: Point[]) {
  if (vertices.length < 3) {
    return false;
  }

  let inside = false;
  for (let index = 0, previousIndex = vertices.length - 1; index < vertices.length; previousIndex = index, index += 1) {
    const current = vertices[index];
    const previous = vertices[previousIndex];
    const crossesY = current.y > point.y !== previous.y > point.y;
    if (crossesY) {
      const xIntersection = ((previous.x - current.x) * (point.y - current.y)) / (previous.y - current.y) + current.x;
      if (point.x < xIntersection) {
        inside = !inside;
      }
    }
  }

  return inside;
}

export type { UseMeasurementOverlayStageHandlersArgs };
